import { css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { AppComponent } from "../component";
import { dom, SplitContainer } from "@omegagrid/core";
import { getLocale, msg } from "@omegagrid/localize";
import { SqlQueryPlan } from "./sqlEditorAdapter";
import { ref, createRef } from "lit/directives/ref.js";
import { showPlan } from '@reporting/html-query-plan';
import { style } from "./sqlEditorQueryPlan.style";
import { computeQueryPlanCosts, formatQueryPlanShare, getQueryPlanCostColor, QueryPlanCosts } from "./sqlEditorQueryPlanCost";

const MIN_PLAN_HEIGHT = 300;

@customElement('rp-editor-sql-plan')
export class SqlEditorQueryPlan extends AppComponent {

	static styles = [css`
		* { box-sizing: border-box; }
		:host {
			display: block;
			height: 100%;
		}

		og-container {
			height: 100%;
		}
	`];

	static registered = false;
	static registerGlobalStyle() {
		if (SqlEditorQueryPlan.registered) return;
		SqlEditorQueryPlan.registered = true;
		dom.appendStyle(document.head, style.cssText);
	}

	splitContainerRef = createRef<SplitContainer>();
	get splitContainer() { return this.splitContainerRef.value! }

	private planComponents = new Map<number, HTMLElement>();
	private planHeaders = new Map<number, HTMLElement>();
	/** costs of the plans received so far - the shares converge as the plans of the batch stream in */
	private costs: QueryPlanCosts = computeQueryPlanCosts([]);

	@property({type: Array})
	plans: SqlQueryPlan[] = [];

	connectedCallback() {
		super.connectedCallback();
		SqlEditorQueryPlan.registerGlobalStyle();
	}

	async willUpdate(props: Map<PropertyKey, unknown>) {
		if (props.has('plans')) {
			this.costs = computeQueryPlanCosts(this.plans.map(plan => plan.xml));
		}
	}

	async updated(props: Map<PropertyKey, unknown>) {
		if (props.has('plans')) this.updateHeaders();
		this.updateSizes();
	}

	private updateSizes() {
		if (!this.splitContainer || this.plans.length === 0) return;
		const height = Math.max(MIN_PLAN_HEIGHT, this.clientHeight / this.plans.length);
		this.splitContainer.items = this.plans.map(() => ({
			size: height,
			collapsed: false
		}));
		dom.setSize(this.splitContainer, {h: height * this.plans.length});
	}

	/**
	 * "Query N: Query cost (relative to the batch): X%" - every plan received changes the
	 * total of the batch, so the headers of all panes are refreshed.
	 */
	private updateHeaders() {
		for (const i of this.planHeaders.keys()) this.updateHeader(i);
	}

	private updateHeader(i: number) {
		const header = this.planHeaders.get(i);
		if (!header) return;
		const cost = this.costs.costs[i] ?? 0;
		header.textContent = msg('Query {n}: Query cost (relative to the batch): {p}', {data: {
			n: i + 1,
			p: formatQueryPlanShare(cost, this.costs.total, getLocale())
		}});
		header.style.backgroundColor = getQueryPlanCostColor(cost, this.costs.max);
	}

	addPlan(plan: SqlQueryPlan) {
		this.plans = [...this.plans, plan];
	}

	clear() {
		this.planComponents.clear();
		this.planHeaders.clear();
		this.plans = [];
	}

	getComponent = (i: number) => {
		if (this.planComponents.has(i)) return this.planComponents.get(i);

		const component = dom.createElement<HTMLDivElement>('div');
		component.className = 'qp-pane';

		const header = dom.createElement<HTMLDivElement>('div');
		header.className = 'qp-pane-header';
		component.appendChild(header);

		const body = dom.createElement<HTMLDivElement>('div');
		body.className = 'qp-pane-body';
		component.appendChild(body);

		const container = dom.createElement<HTMLDivElement>('div');
		container.innerHTML = `<style>${style.cssText}</style>`;
		body.appendChild(container);

		const div = dom.createElement<HTMLDivElement>('div');
		container.appendChild(div);

		// while this pane can consume the wheel scroll, keep it from
		// also scrolling the outer og-container (plan stack)
		component.addEventListener('wheel', (e: WheelEvent) => {
			const down = e.deltaY > 0 && body.scrollTop + body.clientHeight < body.scrollHeight - 1;
			const up = e.deltaY < 0 && body.scrollTop > 0;
			if (down || up) e.stopPropagation();
		}, {passive: true});

		setTimeout(() => {
			showPlan(div, this.plans[i].xml, {jsTooltips: true});
		}, 100);

		this.planComponents.set(i, component);
		this.planHeaders.set(i, header);
		this.updateHeader(i);
		return this.planComponents.get(i);
	}

	render = () => html`
		<og-container>
			<og-splitcontainer ${ref(this.splitContainerRef)}
				slot="content"
				.createComponent="${this.getComponent}"
				orientation="vertical">
			</og-splitcontainer>
		</og-container>
	`;

	layout() {
		this.updateSizes();
	}

}
